import { ListItemIcon, ListItemText, MenuItem } from "@mui/material";
import { useState } from "react";

import AppIcon from "@/common/components/Icons/AppIcon";
import MenuWithTrigger from "@/common/components/Menu/MenuWithTrigger";
import { EntityHelper } from "@/common/helpers/entity";
import { StringHelper } from "@/common/helpers/string";
import { useApiRequest } from "@/common/hooks/api/useApiRequest";
import { useDisplayProps } from "@/common/hooks/useDisplayProps";
import { GeneralStatusHistoryDtoAll } from "@/common/mapping/mappers/generalStatusHistory";
import { ApiEnumName, enumService } from "@/common/services/enum";
import { apiClient } from "@/core/api/ApiClient";
import { GeneralStatusHistoryDto } from "@/core/api/generated/v1";

import BaseEntityInline, {
  BaseEntityInlineInheritableProps,
} from "../../components/EntityInline/BaseEntityInline";
import { EntityInlineActionIcon } from "../../components/EntityInline/EntityInlineActionIcon";
import { EntityInlineActionIconButton } from "../../components/EntityInline/EntityInlineActionIconButton";
import { EntityInlineActions } from "../../components/EntityInline/EntityInlineActions";
import GeneralStatusHistoryViewModal from "./View/GeneralStatusHistoryViewModal";

const defaultDisplayProps = { statusHistory: true };
type DisplayProps = typeof defaultDisplayProps;

export const getGeneralStatusHistoryInlineTitle = (
  data: Nil<GeneralStatusHistoryDtoAll>,
  displayProps?: DisplayProps,
) =>
  data
    ? StringHelper.join(
        [
          `${enumService.getEnumValueNameUntyped(data.statusEnumTypeName || "", data.status)}${data.isCurrent ? " (current)" : ""} (${enumService.getEnumValueName(
            ApiEnumName.GeneralStatusIdentifier,
            data.statusIdentifier,
          )})`,
        ],
        ", ",
        {
          skipEmpty: true,
        },
      )
    : undefined;

export interface GeneralStatusHistoryInlineProps extends BaseEntityInlineInheritableProps {
  entity: GeneralStatusHistoryDto | null | undefined;
  entityId?: string | null;
  displayProps?: Partial<DisplayProps>;
}

/** Inline brief information about the entity. */
export default function GeneralStatusHistoryInline({
  entity,
  entityId,
  displayProps,
  withActions,
  withMenu,
  viewProps,
  ...otherProps
}: GeneralStatusHistoryInlineProps) {
  const displayPropsComputed = useDisplayProps([defaultDisplayProps, displayProps]);

  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  const request = useApiRequest(
    apiClient.generalStatusHistoryApi.apiV1GeneralStatusHistoryGeneralStatusHistoryIdGet,
    {
      nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
      generalStatusHistoryId: entityId!,
    },
    {
      deps: [entityId],
      skip: !entityId || !!entity,
    },
  );
  entity = request?.data || entity;

  const entityIdComputed = entity?.id || entityId;
  const full = EntityHelper.nullIfNotFull(entity);

  return (
    <>
      <BaseEntityInline
        entity={entity}
        entityId={entityId}
        icon={<AppIcon of='history' inText />}
        content={
          getGeneralStatusHistoryInlineTitle(entity, displayPropsComputed) ||
          "Status history record"
          // entity?.statusIdentifier && entity?.status ? (
          //   <GeneralStatusDisplay
          //     identifier={entity.statusIdentifier}
          //     value={entity.status}
          //     enumInlineProps={{ withDescription: false, withHelperTooltip: false }}
          //   />
          // ) : (
          //   getGeneralStatusHistoryInlineTitle(entity, displayPropsComputed) ||
          //   "Status history record"
          // )
        }
        withActions={withActions}
        withMenu={withMenu}
        actions={
          !withActions ? undefined : (
            <EntityInlineActions>
              <EntityInlineActionIconButton
                onClick={() => setIsViewModalOpen(true)}
                tooltipProps={{ title: "Quick view" }}
              >
                <EntityInlineActionIcon of='quickView' />
              </EntityInlineActionIconButton>
            </EntityInlineActions>
          )
        }
        menu={
          !withMenu ? undefined : (
            <MenuWithTrigger
              withAuthCloseOnClick
              trigger={
                <EntityInlineActionIconButton>
                  <EntityInlineActionIcon of='moreVert' />
                </EntityInlineActionIconButton>
              }
            >
              <MenuItem dense onClick={() => setIsViewModalOpen(true)}>
                <ListItemIcon>
                  <AppIcon of='quickView' fontSize='small' />
                </ListItemIcon>
                <ListItemText>Quick view</ListItemText>
              </MenuItem>
            </MenuWithTrigger>
          )
        }
        {...otherProps}
        tooltipTitle={
          otherProps?.tooltipTitle ||
          StringHelper.join(
            [
              "Status history record",
              enumService.getEnumValueName(
                ApiEnumName.GeneralStatusIdentifier,
                full?.statusIdentifier,
              ),
              enumService.getEnumValueNameUntyped(full?.statusEnumTypeName || "", full?.status),
            ],
            ", ",
            { skipEmpty: true },
          )
        }
      />

      {(withActions || withMenu) && (
        <>
          {/* View modal */}
          {entityIdComputed && (
            <GeneralStatusHistoryViewModal
              open={isViewModalOpen}
              onClose={() => setIsViewModalOpen(false)}
              viewProps={{
                generalStatusHistoryId: entityIdComputed,
                generalStatusHistory: EntityHelper.nullIfNotFull(entity),
                onUpdate: viewProps?.onUpdate || null,
                onDelete: viewProps?.onDelete || null,
              }}
            />
          )}
        </>
      )}
    </>
  );
}
